import React, { useEffect, useState } from "react";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from "recharts";
import ValetApi from "../api/Api";
import ValetActivityTooltip from "./ValetActivityTooltip";
import { buildChartDateContextLabel } from "./chartDateContextLabel";
import "./Graph.css";

const PIE_COLORS = ["#93B249", "#0697f7", "#f7b209", "#d32f2f", "#1976d2", "#43a047", "#ffa000", "#6d4c41", "#757575"];

function getUserColorMap(users) {
    const map = {};
    users.forEach((u, idx) => {
        const key = `${u.firstName}`;
        map[key] = PIE_COLORS[idx % PIE_COLORS.length];
    });
    return map;
}

const ValetActivityPieChartsSingle = ({ podiumId, selectedDate = null, startDate = null, endDate = null, userTimeZone = "UTC" }) => {
    const [activityData, setActivityData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const dateContextLabel = buildChartDateContextLabel({ selectedDate, startDate, endDate });

    useEffect(() => {
        if (!podiumId) return;
        setLoading(true);
        setError(null);

        const hasDateRange = Boolean(startDate && endDate);
        const request = hasDateRange ? ValetApi.getValetActivityByPodiumIdDateRange(podiumId, userTimeZone, startDate, endDate) : ValetApi.getTodayValetActivityByPodiumId(podiumId, userTimeZone, selectedDate);

        request
            .then((res) => {
                setActivityData(res?.data?.userTransactionCount || []);
                setLoading(false);
            })
            .catch(() => {
                setError("Failed to load data. Please try again later.");
                setActivityData([]);
                setLoading(false);
            });
    }, [podiumId, selectedDate, startDate, endDate, userTimeZone]);

    const userColorMap = getUserColorMap(activityData);

    const pieData = activityData
        .map((u) => {
            const parked = Number(u.parkedTransactionCount || 0);
            const retrieved = Number(u.retrievedTransactionCount || 0);
            return {
                name: `${u.firstName}`,
                value: parked + retrieved,
                firstParkTransactionTime: u.firstParkTransactionTime,
                lastParkTransactionTime: u.lastParkTransactionTime,
                firstRetrieveTransactionTime: u.firstRetrieveTransactionTime,
                lastRetrieveTransactionTime: u.lastRetrieveTransactionTime,
                firstTransactionTime: u.firstTransactionTime,
                lastTransactionTime: u.lastTransactionTime,
                Parked: parked,
                Retrieved: retrieved,
            };
        })
        .filter((u) => u.value > 0);

    if (loading) {
        return <div className="valet-activity-no-data">Loading...</div>;
    }

    if (error) {
        return <div className="valet-activity-error">{error}</div>;
    }

    if (!Array.isArray(pieData) || pieData.length === 0) {
        return <div className="valet-activity-no-data">No data available for this chart.</div>;
    }

    return (
        <div className="valet-activity-chart-row">
            <div className="valet-activity-chart-card">
                <h4 className="valet-activity-chart-title">Valet Activity (Pie Chart)</h4>
                <div className="valet-activity-chart-context">Date: {dateContextLabel}</div>
                <div className="valet-activity-chart-viewport">
                    <ResponsiveContainer
                        width="100%"
                        height="100%"
                        minWidth={0}
                        debounce={300}>
                        <PieChart>
                            <Pie
                                data={pieData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={90}
                                label={({ name, value, percent }) => `${name} - ${value} (${(percent * 100).toFixed(0)}%)`}>
                                {pieData.map((entry) => (
                                    <Cell
                                        key={`cell-${entry.name}`}
                                        fill={userColorMap[entry.name]}
                                    />
                                ))}
                            </Pie>
                            <Tooltip content={<ValetActivityTooltip />} />
                            <Legend
                                verticalAlign="bottom"
                                height={36}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
};

export default ValetActivityPieChartsSingle;
